import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { awayCheckText, parseFarM, placeCheckBody } from "./mailSettings";
import { SkeletonSection } from "./Skeleton";

/** Rule D-19 and SO-1382: the away check on or off, and its distance from the stop pin. */
export function PlaceCheckSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.PlaceCheckSetting | "failed" | "none" | undefined>(undefined);
  const [farM, setFarM] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const status = useRef<HTMLParagraphElement>(null);

  const show = (next: api.PlaceCheckSetting) => {
    setFound(next);
    setFarM(String(next.far_m));
    setEnabled(next.enabled !== false);
  };

  useEffect(() => {
    user
      .getIdToken()
      .then(api.placeCheck)
      .then(show, (e: unknown) => {
        console.warn("place check: load failed", e);
        setFound(e instanceof api.ApiError && e.status === 404 ? "none" : "failed");
      });
  }, [user]);

  if (found === undefined) return <SkeletonSection title="Away check" rows={4} />;
  if (found === "none") return null;
  if (found === "failed") {
    return (
      <fieldset>
        <legend>Away check</legend>
        <p className="error">Cannot load the away check. Try again later.</p>
      </fieldset>
    );
  }
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const value = parseFarM(farM, found.min, found.max);
    if (typeof value === "string") {
      setMessage(null);
      setProblem(value);
      input.current?.focus();
      return;
    }
    setProblem(null);
    setSaving(true);
    setMessage(null);
    const started = performance.now();
    try {
      const next = await api.setPlaceCheck(await user.getIdToken(), placeCheckBody(found, value, enabled));
      console.info(`place check: saved in ${Math.round(performance.now() - started)} ms`);
      show(next);
      setMessage({ ok: true, text: `Saved. ${awayCheckText(next.enabled, next.far_m)}` });
      requestAnimationFrame(() => status.current?.focus());
    } catch (e) {
      console.warn("place check: save failed", e);
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The away check was not saved. Try again." });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} noValidate data-testid="place-check">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Away check</legend>
        <label className="check">
          <input
            type="checkbox"
            data-testid="place-check-on"
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              setMessage(null);
            }}
          />{" "}
          Ask the driver when an entry is far from the stop
        </label>
        <div className="limit">
          <label htmlFor="place-check-far">Distance from the stop pin (m)</label>
          <input
            id="place-check-far"
            data-testid="place-check-far"
            ref={input}
            inputMode="numeric"
            value={farM}
            disabled={!enabled}
            aria-invalid={problem !== null || undefined}
            aria-describedby={problem ? "place-check-error place-check-hint" : "place-check-hint"}
            onChange={(e) => {
              setFarM(e.target.value);
              setMessage(null);
              setProblem(null);
            }}
          />
          {problem && (
            <p id="place-check-error" role="alert" className="error" data-testid="place-check-error">
              {problem}
            </p>
          )}
          <p id="place-check-hint" className="hint">
            {awayCheckText(found.enabled, found.far_m)} Default: {found.default} m.
          </p>
        </div>
        {found.can_edit && (
          <div className="actions">
            <button className="primary" data-testid="save-place-check">
              {saving ? "Saving…" : "Save the away check"}
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="place-check-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </form>
  );
}
